import { defineComponent, ref } from "vue";
import Multiselect from "vue-multiselect";
import Svg from "../../../../core/svg";
import { browseStore } from "../../store";
import SearchContainer from "../default/searchContainer";
import { Classes, isSmallScreen } from "./common";
import listHeaderDesktop from "./desktop/listHeader";
import listHeaderMobile from "./mobile/listHeader";
import { DistrictColumns, JobPosting, JobPostingState, JobPostingStore } from "./types";

export default defineComponent(
    {
        name: "listHeader",
        setup()
        {
            const state = browseStore.injectBrowseState<JobPostingStore, JobPosting>();
            if (!state)
            {
                window.skyward.error.reportException(new ReferenceError("Unable to retrieve browse state"));
                return () => null;
            }

            const dataSource = state.features.dataSource;

            return isSmallScreen()
                ? listHeaderMobile(dataSource)
                : listHeaderDesktop(dataSource);
        }
    });

export function getJobTitleSearchDiv(dataSource: JobPostingState, divClass: string, searchContainerClass: string): JSX.Element
{
    const searchButtonCallback = function (searchValue: string)
    {
        dataSource.jobTitleSearchValue = searchValue;
    }

    return (<div class={divClass}>
        <label class="jobPostingFilterLabel">Job Title</label>
        <SearchContainer
            searchContainerID="JobPostingSearchContainer"
            searchBoxID="jobTitleSearchBox"
            searchContainerClasses={searchContainerClass}
            placeholderText="Search Job Title"
            onSearch={searchButtonCallback}
            value={dataSource.jobTitleSearchValue}
        />
    </div>);
}

export function getMultiSelectFilters(dataSource: JobPostingState, multiSelectClassName: string, filterDivClassName: string, isMobile: boolean = false): JSX.Element[]
{
    const getMultiSelects = function (): JSX.Element[]
    {
        const getMultiSelect = function (label: string, vmodelName: keyof typeof dataSource, options: string[]): JSX.Element
        {
            const getLimitText = function (numberSelectedOverLimit: number): string
            {
                return "+" + numberSelectedOverLimit;
            }

            const select = ref<Multiselect>();
            return (
                <div class={multiSelectClassName}>
                    <label class="jobPostingFilterLabel">{label}</label>
                    <Multiselect ref={select}
                        id={label}
                        v-model={dataSource[vmodelName]}
                        options={options}
                        multiple={true}
                        limit={1}
                        limit-text={getLimitText}
                        close-on-select={false}
                        clear-on-select={false}
                        preserve-search={true}
                        placeholder={dataSource[vmodelName].length === 0 ? `Filter by ${label}` : ''}
                        showLabels={false}
                        openDirection="below"
                        prevent-autofocus={isMobile}
                        v-slots={
                            {
                                option: (props: { option: typeof options[0] }) =>
                                (
                                    <div>
                                        {dataSource[vmodelName].includes(props.option) ? <input type="checkbox" class="form-check-input" checked /> : <input type="checkbox" class="form-check-input" />}
                                        <span>{props.option}</span>
                                    </div>
                                )
                            }
                        } />
                </div>);
        }

        let filters: JSX.Element[] = [];
        for (const districtColumn of dataSource.orderedDistrictColumns)
        {
            switch (districtColumn)
            {
                case DistrictColumns.Location:
                    filters.push(getMultiSelect("Location", "selectedLocations", dataSource.locations));
                    break;
                case DistrictColumns.JobCategory:
                    filters.push(getMultiSelect("Job Category", "selectedJobCategories", dataSource.jobCategories));
                    break;
                case DistrictColumns.EmploymentType:
                    filters.push(getMultiSelect("Employment Type", "selectedEmploymentTypes", dataSource.employmentTypes));
                    break;
                case DistrictColumns.City:
                    filters.push(getMultiSelect("City", "selectedCities", dataSource.cities));
                    break;
                case DistrictColumns.ZIPCode:
                    filters.push(getMultiSelect("ZIP Code", "selectedZipCodes", dataSource.zipCodes));
                    break;
                case DistrictColumns.JobType:
                    filters.push(getMultiSelect("Job Type", "selectedJobTypeDescriptions", dataSource.jobTypeDescriptions));
                    break;
            }
        }
        return filters;
    }

    const filters = getMultiSelects();

    if (filters.length > 0)
    {
        let filtersControlRef = ref<HTMLElement>();
        let filtersRef = ref<HTMLElement>();

        const getJobPostingFilterControlDiv = function ()
        {
            const jobPostingFilterControlID = "JobPostingFilterControl";
            const getFilterBoxText = function (): string
            {
                let text = '';
                let addSection = (selections: Array<any>, label: string): string => text += (selections.length > 0 ? `${selections.length} ${label}; ` : '');

                for (const districtColumn of dataSource.orderedDistrictColumns)
                {
                    switch (districtColumn)
                    {
                        case DistrictColumns.Location:
                            addSection(dataSource.selectedLocations, "Locations");
                            break;
                        case DistrictColumns.JobCategory:
                            addSection(dataSource.selectedJobCategories, "Job Categories");
                            break;
                        case DistrictColumns.EmploymentType:
                            addSection(dataSource.selectedEmploymentTypes, "Employment Types");
                            break;
                        case DistrictColumns.City:
                            addSection(dataSource.selectedCities, "Cities");
                            break;
                        case DistrictColumns.ZIPCode:
                            addSection(dataSource.selectedZipCodes, "ZIP Codes");
                            break;
                        case DistrictColumns.JobType:
                            addSection(dataSource.selectedJobTypeDescriptions, "Job Types");
                            break;
                    }
                }

                return text == '' ? "Select Filters" : `Selected: ${text}`;
            }

            const createFilterModal = function ()
            {
                const positionModal = function ()
                {
                    let control = $("#" + jobPostingFilterControlID);
                    let offset = control.offset();
                    if (offset != undefined)
                    {
                        let rawHeight = control.height();
                        let height = ((rawHeight != undefined) ? rawHeight : 0);
                        let top = offset.top + height;
                        let maxHeight = (window.innerHeight - top);
                        $("#JobPostingFilters").css({
                            top: top,
                            left: offset.left,
                            position: "absolute",
                            display: "block",
                            "overflow-y": "auto",
                            "overflow-x": "hidden",
                            "max-height": maxHeight
                        });
                    }
                }
                const selectArrow = $("#FilterControlSelectDiv");
                const closeModal = function ()
                {
                    window.skyward.window.removeLayoutResizeCallback(positionModal);
                    selectArrow.removeClass("multiselect--active");
                }
                if (filtersRef.value != undefined)
                {
                    window.skyward.modalLayer.newModalLayer(filtersRef.value, closeModal);
                    window.skyward.window.registerLayoutResizeCallback(positionModal);
                    positionModal();
                    selectArrow.addClass("multiselect--active");
                }
            }

            return (
                <div ref={filtersControlRef} id={jobPostingFilterControlID} class={filterDivClassName}>
                    <label class="jobPostingFilterLabel">Filtering</label>
                    <div onClick={createFilterModal} tabindex="-1" class="multiselect" role="combobox">
                        <div id="FilterControlSelectDiv"><div class="multiselect__select"></div></div>
                        <div class="multiselect__tags">
                            <span class="multiselect__placeholder">{getFilterBoxText()}</span>
                        </div>
                    </div>
                </div>
            )
        }

        return [
            getJobPostingFilterControlDiv(),
            <div ref={filtersRef} id="JobPostingFilters" class={Classes.DisplayNone}>{filters}</div>
        ];
    }
    return [];
}

export function getFilterControlButtonContainer(dataSource: JobPostingState, classes: { [className: string]: boolean } | string | undefined = undefined, resetFirst: boolean): JSX.Element
{
    const resetButtonCallback = function ()
    {
        //The names in these searchParams need to be kept in sync with applicantTracking.ts as well as the JobBoardController where we instantiate the datasrouce
        const url = new URL(window.location.href);
        let jobTitle = url.searchParams.get("JobTitle") ?? "";

        const jobTitleSearchInputElement = document.getElementById("jobTitleSearchBox") as HTMLInputElement;
        jobTitleSearchInputElement.value = jobTitle;
        dataSource.jobTitleSearchValue = jobTitle;
        dataSource.selectedLocations = url.searchParams.getAll("Location");
        dataSource.selectedJobCategories = url.searchParams.getAll("JobCategory");
        dataSource.selectedEmploymentTypes = url.searchParams.getAll("EmploymentType");
        dataSource.selectedCities = url.searchParams.getAll("City");
        dataSource.selectedZipCodes = url.searchParams.getAll("ZIPCode");
        dataSource.selectedJobTypeDescriptions = url.searchParams.getAll("JobType");
        //This sort collection is static and needs to be kept in sync with the sort collection in the JobPostingDataSource constructor when we build the VueBrowse (JobBoardController.cs)
        dataSource.sorts = { "ApplicationStatus": true, "IsFavoriteJobPosting": true, "IsFeatured": true, "JobTitle": false }
    }

    const clearButtonCallback = function ()
    {
        const jobTitleSearchInputElement = document.getElementById("jobTitleSearchBox") as HTMLInputElement;
        jobTitleSearchInputElement.value = "";
        dataSource.jobTitleSearchValue = "";
        dataSource.selectedLocations = [];
        dataSource.selectedJobCategories = [];
        dataSource.selectedEmploymentTypes = [];
        dataSource.selectedCities = [];
        dataSource.selectedZipCodes = [];
        dataSource.selectedJobTypeDescriptions = [];
    }

    const getReset = function (): JSX.Element
    {
        //we aren't following BEM naming here because we have a system-wide style class on SVGs that we need to override
        return (<a onClick={resetButtonCallback} class={classes}>
            <Svg id="JobPostingReset" name="UpdateBack" /> 
            <span>Reset</span>
        </a>)
    }

    const getClear = function (): JSX.Element
    {
        //we aren't following BEM naming here because we have a system-wide style class on SVGs that we need to override
        return (<a onClick={clearButtonCallback} class={classes}>
                <Svg id="JobPostingClear" name="Clear" />
                <span>Clear Filters</span>
            </a>)
    }

    if (resetFirst)
    {
        return (<span>
            {getReset()}
            {getClear()}
            </span>)
    }
    else
    {
        return (<span>
            {getClear()}
            {getReset()}
            </span>)
    }
}